// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xHeaderState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import XngBookingAppointment from "@bgs/App/Components/XngBookingAppointment";
import useGoogleMaps from "@insite/client-framework/Common/Hooks/useGoogleMaps";
import useLocationFilterSearch from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import { GetDealersApiParameter } from "@insite/client-framework/Services/DealerService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getDealersDataView,
    getDealersDefaultLocation,
} from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import loadDealers from "@insite/client-framework/Store/Data/Dealers/Handlers/LoadDealers";
import setParameter from "@insite/client-framework/Store/Pages/LocationFinder/Handlers/SetParameter";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & xHeaderState) => {
    const getDealersParameter = state.pages.locationFinder.getDealersParameter;
    const currentWarehouseId = state.context.session.pickUpWarehouse?.id;
    return {
        currentWarehouseId,
        settings: getSettingsCollection(state),
        getDealersParameter,
        dealersDataView: getDealersDataView(state, {
            ...(getDealersParameter || {}),
            pageSize: 1000,
            additionalQueryStringParameters: {
                ...(getDealersParameter?.additionalQueryStringParameters || {}),
                currentWarehouseId,
            },
        } as any),
        dealersDefaultLocation: getDealersDefaultLocation(state),
        isSticky: state.xHeader.isSticky,
    };
};

const mapDispatchToProps = {
    loadDealers,
    setParameter,
};

export interface XngBookingAppointmentDrawerStyles {
    drawer?: DrawerPresentationProps;
    titleClickable?: ClickablePresentationProps;
}

export const xngBookingAppointmentDrawerStyles: XngBookingAppointmentDrawerStyles = {
    titleClickable: {
        css: css`
            padding: 19px;
            background: ${getColor("secondary")};
            font-size: ${fontConfig.fontSize.small}px;
            font-weight: ${fontConfig.weight.semiBold};
            letter-spacing: 1.08px;
            line-height: 17px;
            text-align: center;
            text-transform: uppercase;
            ${({ isSticky }: any) =>
                isSticky &&
                `
            width: 100%;
            `}
            &:hover {
                background: ${getColor("text.main")};
                color: ${getColor("common.white")};
            }
        `,
    },
    drawer: {
        cssOverrides: {
            drawerContent: css`
                height: 100%;
            `,
            drawerBody: css`
                width: 360px;
                @media (max-width: 425px) {
                    width: 100%;
                }
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    useLoadScript: ({ googleMapsApiKey }: { googleMapsApiKey: any }) => { isLoaded: boolean };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = xngBookingAppointmentDrawerStyles;
const XngBookingAppointmentDrawer: FC<Props> = ({
    settings,
    dealersDataView,
    dealersDefaultLocation,
    getDealersParameter,
    loadDealers,
    setParameter,
    useLoadScript,
    currentWarehouseId,
    isSticky,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (getDealersParameter) {
            loadDealers({
                ...getDealersParameter,
                pageSize: 1000,
                additionalQueryStringParameters: {
                    ...getDealersParameter.additionalQueryStringParameters,
                    currentWarehouseId,
                },
            });
        }
    }, [getDealersParameter]);

    // Manage Google Maps State
    const googleMapsApiKey = (settings.customSettings as any).storefrontGoogleMapsAPIKey;
    const { isLoaded } = useLoadScript({ googleMapsApiKey });
    const {
        googleMap,
        setGoogleMap,
        currentLocation,
        setCurrentLocation,
        isGoogleMapsScriptsLoaded,
        setBounds,
        setMapCenter,
        locationKnown,
    } = useGoogleMaps({
        isShown: true,
        isLoaded,
    });

    // Manage Selected Dealer
    const [selectedDealer, setSelectedDealer] = React.useState<DealerModel | undefined>(undefined);
    const [showSelectedDealer, setShowSelectedDealer] = React.useState<boolean>(false);
    const [defaultRadius, setDefaultRadius] = React.useState<number>(0);

    // Manage Local Dealers
    const [dealers, setDealers] = React.useState<DealerModel[]>([]);
    const [resultCount, setResultCount] = React.useState<number>(0);

    // Manage Loading Dealers on State Changes
    const onSearch = (searchFilter: string) => {
        setLocationSearchFilter(searchFilter);
    };

    const createFilter = (coords: google.maps.LatLng | undefined, searchFilter: string): GetDealersApiParameter => {
        // Default center Point
        let centerPoint = { latitude: 0, longitude: 0 };
        // Passed in coords
        if (coords && coords.lat() !== 0 && coords.lng() !== 0) {
            centerPoint = {
                latitude: coords.lat(),
                longitude: coords.lng(),
            };
        }
        //  Selected Dealer
        if (centerPoint.latitude === 0 && centerPoint.longitude === 0 && selectedDealer) {
            centerPoint = {
                ...selectedDealer,
            };
        }
        // default Location
        if (
            centerPoint.latitude === 0 &&
            centerPoint.longitude === 0 &&
            dealersDefaultLocation.latitude !== 0 &&
            dealersDefaultLocation.latitude !== 0
        ) {
            centerPoint = dealersDefaultLocation;
        }

        return {
            name: searchFilter,
            latitude: centerPoint.latitude,
            longitude: centerPoint.longitude,
            page,
            pageSize,
        };
    };

    const handleLoadLocations = (parameter: GetDealersApiParameter) => {
        setParameter(parameter);
    };

    const { doSearch, locationSearchFilter, setLocationSearchFilter, page, setPage, pageSize, setPageSize } =
        useLocationFilterSearch<DealerModel, GetDealersApiParameter>({
            onSearch,
            loadLocations: handleLoadLocations,
            currentLocation,
            defaultRadius,
            selectedLocation: selectedDealer,
            setSelectedLocation: location => setSelectedDealer(location),
            setShowSelectedLocation: setShowSelectedDealer,
            createFilter,
        });
    React.useEffect(() => {
        if (!isGoogleMapsScriptsLoaded) {
            return;
        }
        if (dealersDataView.isLoading) {
            setIsLoading(true);
        }
        if (dealersDataView.value && !dealersDataView.isLoading) {
            const { pagination, value: dealers, defaultRadius, defaultLatitude, defaultLongitude } = dealersDataView;
            // Find default location
            setLocationSearchFilter(locationSearchFilter);
            setResultCount(pagination?.totalItemCount || 0);
            setDefaultRadius(defaultRadius);
            setMapCenter(new google.maps.LatLng(defaultLatitude, defaultLongitude));
            setDealers(dealers || []);
            setIsLoading(false);
        }
    }, [dealersDataView, isGoogleMapsScriptsLoaded]);

    const handleCloseModal = () => {
        setTimeout(() => {
            setIsOpen(false);
        }, 150);
    };
    const handleOpenModal = () => {
        setTimeout(() => {
            setIsOpen(true);
        }, 150);
    };
    return (
        <>
            <Clickable
                {...styles.titleClickable}
                data-test-selector="bookingAppointmentLink"
                onClick={handleOpenModal}
                {...({ isSticky } as any)}
            >
                {translate("Book Appointment")}
            </Clickable>
            <Drawer
                draggable={false}
                position={isSticky ? "left" : "right"}
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={handleCloseModal}
                contentLabel="booking appointment drawer"
            >
                <XngBookingAppointment dealers={dealers} isLoading={isLoading} />
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withDynamicGoogleMaps(XngBookingAppointmentDrawer)),
    definition: {
        displayName: "Booking Appointment Link",
        icon: "Link",
        fieldDefinitions: [],
        group: "XNGAGE" as any,
    },
};

export default widgetModule;
